import { KakiMakanSectionAndSubSectionsUniqueNames } from "@config/kakiMakan";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import { CasBannerWidget } from "@routes/Home/Components/CasBannerWidget";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import KakiMakanRestaurants from "./Components/KakiMakanRestaurants";
import KakiMakanVideoSection from "./Components/KakiMakanVideoSection";
import {
  fetchKakiMakanSectionData,
  KakiMakanPayloadResponseType,
} from "./KakiMakan.server";
import KakiMakanMetaData from "./KakiMakanMetaData";

export function meta() {
  return [getMetaData(KakiMakanMetaData().metaTags)];
}

/**
 * KakiMakanSectionPage
 * Description: [Renders the Kaki Makan landing page with articles and video section]
 * @returns The rendered React element
 */
export default function KakiMakanSectionPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    KakiMakanPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const {
    data: kakiMakanArticles,
    initialRatings,
    kakiMakanBanner,
  } = dataLoaderResponse.payload;

  const SectionName = KakiMakanSectionAndSubSectionsUniqueNames[0];
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    adsSectionName: SectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideScrollableSectionMenu: true,
    hideNavigationInMobile: true,
  };

  return (
    <BHBasePage {...basePageProps}>
      <WidgetWrapper>
        <div className="flex flex-col gap-4 bg-white-100">
          <CasBannerWidget CasBannerWidgetContent={kakiMakanBanner} />
          <div className="flex flex-col gap-4 p-1.5 lg:p-4 items-center">
            <KakiMakanRestaurants
              kakiMakanArticles={kakiMakanArticles}
              initialRatings={initialRatings}
            />
          </div>
          <KakiMakanVideoSection />
        </div>
      </WidgetWrapper>
    </BHBasePage>
  );
}

export async function loader() {
  const data = await fetchKakiMakanSectionData();
  const { gaData } = KakiMakanMetaData();

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<KakiMakanPayloadResponseType, string>
  >;
}
